import { useEffect } from "react";
import {
  InfiniteListBase,
  useGetIdentity,
  useListContext,
  useTranslate,
} from "ra-core";
import { BulkActionsToolbar } from "@/components/admin/bulk-actions-toolbar";
import { BulkDeleteButton } from "@/components/admin/bulk-delete-button";
import { CreateButton } from "@/components/admin/create-button";
import { FilterButton } from "@/components/admin/filter-form";
import { List } from "@/components/admin/list";
import { SelectAllButton } from "@/components/admin/select-all-button";
import { SortButton } from "@/components/admin/sort-button";
import { Card } from "@/components/ui/card";

import { BulkTagButton } from "./BulkTagButton";
import { ContactEmpty } from "./ContactEmpty";
import { DataImportButton } from "../dataImport/DataImportButton";
import {
  ContactListContent,
  ContactListContentMobile,
} from "./ContactListContent";
import {
  ContactListFilterSummary,
  ContactListFilter,
} from "./ContactListFilter";
import { InitialDisplayedFilters } from "../filters/InitialDisplayedFilters";
import { TopToolbar } from "../layout/TopToolbar";
import { DEFAULT_CONTACT_FILTERS, useContactFilters } from "./contactFilters";
import { useContactColumns } from "../spreadsheet/contactColumns";
import { ExcelExportButton } from "../spreadsheet/ExcelExportButton";
import { InfinitePagination } from "../misc/InfinitePagination";
import MobileHeader from "../layout/MobileHeader";
import { MobileContent } from "../layout/MobileContent";

export const ContactList = () => {
  const { identity } = useGetIdentity();
  const translate = useTranslate();
  const filters = useContactFilters();

  if (!identity) return null;

  return (
    <List
      title={translate("resources.contacts.name", { smart_count: 2 })}
      filters={filters}
      actions={<ContactListActions />}
      perPage={25}
      sort={{ field: "last_seen", order: "DESC" }}
    >
      <ContactListLayoutDesktop />
    </List>
  );
};

const ContactListLayoutDesktop = () => {
  const { data, isPending, filterValues } = useListContext();

  const hasFilters = filterValues && Object.keys(filterValues).length > 0;

  if (isPending) return null;

  if (!data?.length && !hasFilters) return <ContactEmpty />;

  return (
    <div className="flex flex-row gap-8">
      <InitialDisplayedFilters sources={DEFAULT_CONTACT_FILTERS} />
      <div className="w-full flex flex-col gap-4">
        <Card className="py-0">
          <ContactListContent />
        </Card>
      </div>
      <BulkActionsToolbar>
        <ContactBulkActionButtons />
      </BulkActionsToolbar>
    </div>
  );
};

const ContactBulkActionButtons = () => {
  const columns = useContactColumns();
  return (
    <>
      <SelectAllButton />
      <BulkTagButton />
      <ExcelExportButton columns={columns} selectedOnly />
      <BulkDeleteButton />
    </>
  );
};

const ContactListActions = () => {
  const columns = useContactColumns();
  return (
    <TopToolbar>
      <SortButton fields={["first_name", "last_name", "last_seen"]} />
      <FilterButton />
      <DataImportButton resource="contacts" />
      <ExcelExportButton columns={columns} />
      <CreateButton />
    </TopToolbar>
  );
};

export const ContactListMobile = () => {
  const { identity } = useGetIdentity();
  if (!identity) return null;

  return (
    <InfiniteListBase
      perPage={25}
      sort={{ field: "last_seen", order: "DESC" }}
      queryOptions={{
        onError: () => {
          /* Disable error notification as ContactListLayoutMobile handles it */
        },
      }}
    >
      <ContactListLayoutMobile />
    </InfiniteListBase>
  );
};

const ContactListLayoutMobile = () => {
  const translate = useTranslate();
  const { isPending, data, error, filterValues, displayedFilters, setFilters } =
    useListContext();

  // No search box here to show or clear a name search set on desktop or in a URL
  const { q, ...filtersWithoutSearch } = filterValues ?? {};
  useEffect(() => {
    if (q != null) {
      setFilters(filtersWithoutSearch, displayedFilters);
    }
  });

  const hasFilters = filterValues && Object.keys(filterValues).length > 0;

  if (!isPending && !data?.length && !hasFilters) return <ContactEmpty />;

  return (
    <div>
      <MobileHeader showRefresh>
        {/* No name search here: the header's global search finds contacts */}
        <div className="flex flex-1 items-center justify-between">
          <h1 className="text-xl font-semibold">
            {translate("resources.contacts.name", { smart_count: 2 })}
          </h1>
          <ContactListFilter />
        </div>
      </MobileHeader>
      <MobileContent>
        <ContactListFilterSummary />
        <ContactListContentMobile />
        {!error && (
          <div className="flex justify-center">
            <InfinitePagination />
          </div>
        )}
      </MobileContent>
    </div>
  );
};
